/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useCallback } from 'react'
import { usePostHog } from '@/contexts/PostHogContext'
import { AnalyticsEvents } from '@/lib/analytics/events'
import { validateEventProperties, sanitizeEventProperties } from '@/lib/analytics/privacy'
import type {
  ProjectEventProperties,
  ClipEventProperties,
  VfxShotEventProperties,
  CommentEventProperties,
  TeamEventProperties,
  TimelineEventProperties,
} from '@/types/analytics'

/**
 * Custom hook for tracking analytics events
 * Provides type-safe methods for common tracking scenarios
 *
 * IMPORTANT: All event properties are automatically validated and sanitized
 * to prevent logging sensitive film industry data (project names, shot names, etc.)
 */
export function useAnalytics() {
  const { track: rawTrack, isReady } = usePostHog()

  /**
   * Privacy-safe track wrapper
   * Validates and sanitizes properties before sending to PostHog
   */
  const track = useCallback((eventName: string, properties?: Record<string, any>) => {
    if (!properties) {
      rawTrack(eventName, properties)
      return
    }

    // In development, throw if sensitive fields are detected
    validateEventProperties(properties)

    // In all environments, sanitize properties as a safety net
    const safeProperties = sanitizeEventProperties(properties)

    rawTrack(eventName, safeProperties)
  }, [rawTrack])

  // Project tracking
  const trackProjectCreated = useCallback((props: ProjectEventProperties) => {
    track(AnalyticsEvents.PROJECT_CREATED, props)
  }, [track])

  const trackProjectViewed = useCallback((props: ProjectEventProperties) => {
    track(AnalyticsEvents.PROJECT_VIEWED, props)
  }, [track])

  const trackProjectDeleted = useCallback((props: ProjectEventProperties) => {
    track(AnalyticsEvents.PROJECT_DELETED, props)
  }, [track])

  // Clip tracking
  const trackClipsImported = useCallback((props: ClipEventProperties) => {
    track(AnalyticsEvents.CLIPS_IMPORTED, props)
  }, [track])

  const trackClipCreated = useCallback((props: ClipEventProperties) => {
    track(AnalyticsEvents.CLIP_CREATED, props)
  }, [track])

  const trackClipUpdated = useCallback((props: ClipEventProperties) => {
    track(AnalyticsEvents.CLIP_UPDATED, props)
  }, [track])

  const trackClipDeleted = useCallback((props: ClipEventProperties) => {
    track(AnalyticsEvents.CLIP_DELETED, props)
  }, [track])

  const trackClipsAssigned = useCallback((props: ClipEventProperties) => {
    track(AnalyticsEvents.CLIPS_ASSIGNED, props)
  }, [track])

  // VFX Shot tracking
  const trackVfxShotCreated = useCallback((props: VfxShotEventProperties) => {
    track(AnalyticsEvents.VFX_SHOT_CREATED, props)
  }, [track])

  const trackVfxShotStatusChanged = useCallback((props: VfxShotEventProperties) => {
    track(AnalyticsEvents.VFX_SHOT_STATUS_CHANGED, props)
  }, [track])

  const trackVfxShotsAssigned = useCallback((props: VfxShotEventProperties) => {
    track(AnalyticsEvents.VFX_SHOTS_ASSIGNED, props)
  }, [track])

  const trackVfxShotsDetected = useCallback((props: VfxShotEventProperties) => {
    track(AnalyticsEvents.VFX_SHOTS_DETECTED, props)
  }, [track])

  // Comment tracking
  const trackCommentCreated = useCallback((props: CommentEventProperties) => {
    track(AnalyticsEvents.COMMENT_CREATED, props)
  }, [track])

  // Team tracking
  const trackTeamMemberInvited = useCallback((props: TeamEventProperties) => {
    track(AnalyticsEvents.TEAM_MEMBER_INVITED, props)
  }, [track])

  const trackTeamMemberRoleChanged = useCallback((props: TeamEventProperties) => {
    track(AnalyticsEvents.TEAM_MEMBER_ROLE_CHANGED, props)
  }, [track])

  // Timeline tracking
  const trackTimelineCommitCreated = useCallback((props: TimelineEventProperties) => {
    track(AnalyticsEvents.TIMELINE_COMMIT_CREATED, props)
  }, [track])

  const trackTimelineComparisonCreated = useCallback((props: TimelineEventProperties) => {
    track(AnalyticsEvents.TIMELINE_COMPARISON_CREATED, props)
  }, [track])

  // Generic feature usage
  const trackFeatureUsed = useCallback((feature: string, properties?: Record<string, any>) => {
    track(AnalyticsEvents.FEATURE_USED, {
      feature_name: feature,
      ...properties,
    })
  }, [track])

  // Search tracking
  const trackSearchPerformed = useCallback((
    searchType: string,
    query: string,
    resultCount: number,
    properties?: Record<string, any>
  ) => {
    track(AnalyticsEvents.SEARCH_PERFORMED, {
      search_type: searchType,
      query_length: query.length,
      result_count: resultCount,
      ...properties,
    })
  }, [track])

  return {
    isReady,
    // Raw track for custom events
    track,
    // Project
    trackProjectCreated,
    trackProjectViewed,
    trackProjectDeleted,
    // Clips
    trackClipsImported,
    trackClipCreated,
    trackClipUpdated,
    trackClipDeleted,
    trackClipsAssigned,
    // VFX Shots
    trackVfxShotCreated,
    trackVfxShotStatusChanged,
    trackVfxShotsAssigned,
    trackVfxShotsDetected,
    // Comments
    trackCommentCreated,
    // Team
    trackTeamMemberInvited,
    trackTeamMemberRoleChanged,
    // Timeline
    trackTimelineCommitCreated,
    trackTimelineComparisonCreated,
    // Generic
    trackFeatureUsed,
    trackSearchPerformed,
  }
}
